import React from "react";
import {
  Menu,
  MenuProps,
  MenuItem,
  Button,
  Divider,
  useTheme,
  Box,
} from "@mui/material";
import { Star } from "lucide-react";
import DoneIcon from "@mui/icons-material/Done";
import {
  BodypartType,
  ExerciseAssetDto,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";
import { BodypartTypeLabels, MovementPatternTypeLabels } from "./constants";
import { CategoryType, CategoryTypeLabel } from "./ManageLibraryExercise";
import { exerciseTagCategoryMultiSelectStyles } from "./exerciseLibraryStyles";
import { UpdateExerciseFunctionType } from "./ExerciseCategoryBadge";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles((theme) => ({
  menuItem: {
    padding: theme.spacing(1),
  },
}));

export interface ExerciseSelectedCategoryProps<T extends CategoryType>
  extends Omit<MenuProps, "open" | "onClose"> {
  label: CategoryTypeLabel;
  showButton?: boolean;
  onClose?: () => void;
  anchorEl?: HTMLElement | null;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  selectedOptions?: T[];
  readonly?: boolean;
  updateExercise?: UpdateExerciseFunctionType;
  showOnlySelected?: boolean;
}

export function ExerciseSelectedCategory<T extends CategoryType>(
  props: ExerciseSelectedCategoryProps<T>,
) {
  const theme = useTheme();
  const s = exerciseTagCategoryMultiSelectStyles();
  const classes = useStyles();
  const {
    label,
    onClose,
    open,
    setOpen,
    anchorEl,
    selectedOptions = [],
  } = props;

  const primaryItem = selectedOptions ? selectedOptions[0] : null;

  const handleClose = () => {
    setOpen?.(false);
  };

  return (
    <>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              minWidth: 300,
              maxHeight: 200,
            },
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        className={s.menu}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <MenuItem className={s.menuHeader}> {`${label}s`}</MenuItem>
        <Divider />
        {selectedOptions.map((option) => {
          const isPrimary = primaryItem === option;

          return (
            <MenuItem key={String(option)} className={classes.menuItem}>
              <Box display={"flex"} alignItems={"center"}>
                {label === CategoryTypeLabel.Bodypart &&
                  BodypartTypeLabels[option as BodypartType]}
                {label === CategoryTypeLabel.MovementPattern &&
                  MovementPatternTypeLabels[option as MovementPatternType]}

                {isPrimary && (
                  <Button
                    className={s.primaryLabel}
                    variant={"contained"}
                    children={"Primary"}
                  />
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
